import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Search, Edit, Visibility } from '@mui/icons-material';
import { useAfiliados } from '../../hooks/useAfiliados';
import { useAuth } from '../../context/AuthContext';
import {
  ESTADO_AFILIADO_LABELS,
  ESTADO_AFILIADO_COLORS,
  ESTADO_AFILIADO_OPTIONS,
  type Afiliado,
  type EstadoAfiliado,
} from '../../types/afiliado';
import { formatCuit } from '../../utils/format';

export default function AfiliadosList() {
  const navigate = useNavigate();
  const { effectiveRole, effectiveEmpresaId } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState<EstadoAfiliado | 'todos'>('todos');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const { data, isLoading, isError, error } = useAfiliados({
    search,
    estado,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    // Contexto de empresa (admin_empresa o admin impersonando): solo su padron.
    // Admin/operador sin impersonar: null -> padron completo (#494).
    empresaId: effectiveEmpresaId,
  });

  // La empresa tambien puede dar de alta afiliados (#495); el backend autoriza
  // via RPC y asocia el afiliado a su empresa.
  const canCreate =
    effectiveRole === 'admin_general' ||
    effectiveRole === 'operador' ||
    effectiveRole === 'admin_empresa';

  const handleSearch = () => setSearch(searchInput);
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const columns = useMemo<GridColDef<Afiliado>[]>(
    () => [
      {
        field: 'cuil',
        headerName: 'CUIL',
        width: 150,
        renderCell: (p) => (
          <Box sx={{ fontFamily: 'monospace' }}>{formatCuit(p.value)}</Box>
        ),
      },
      {
        field: 'apellido',
        headerName: 'Apellido',
        flex: 1,
        minWidth: 160,
        renderCell: (p) => <Box sx={{ fontWeight: 500 }}>{p.value}</Box>,
      },
      { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 160 },
      {
        field: 'telefono',
        headerName: 'Telefono',
        width: 140,
        renderCell: (p) => p.value || '—',
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => p.value || '—',
      },
      {
        field: 'estado',
        headerName: 'Estado',
        width: 110,
        renderCell: (p: GridRenderCellParams<Afiliado>) => (
          <Chip
            label={ESTADO_AFILIADO_LABELS[p.value as EstadoAfiliado]}
            size="small"
            color={ESTADO_AFILIADO_COLORS[p.value as EstadoAfiliado]}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Afiliado>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver detalle">
              <IconButton size="small" onClick={() => navigate(`/afiliados/${p.row.id}`)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() => navigate(`/afiliados/${p.row.id}/editar`)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Afiliados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Padron de trabajadores afiliados al sindicato
          </Typography>
        </Box>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/afiliados/nuevo')}
          >
            Nuevo Afiliado
          </Button>
        )}
      </Stack>

      <Card sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ p: 2 }}
        >
          <TextField
            size="small"
            placeholder="Buscar por apellido, nombre o CUIL..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            select
            label="Estado"
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value as EstadoAfiliado | 'todos');
              setPaginationModel((m) => ({ ...m, page: 0 }));
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            {ESTADO_AFILIADO_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={handleSearch} startIcon={<Search />}>
            Buscar
          </Button>
        </Stack>
      </Card>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar afiliados: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data ?? []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          paginationMode="server"
          rowCount={data?.count ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default' },
          }}
          localeText={{
            noRowsLabel: 'No hay afiliados registrados',
            footerTotalRows: 'Total:',
            MuiTablePagination: {
              labelRowsPerPage: 'Por pagina:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
            },
          }}
        />
      </Card>
    </Box>
  );
}
